import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { Table } from "@/components/ui/Table";
import { CreateArticleCategoryModal } from "@/pages/stammdaten/CreateArticleCategoryModal";
import { EditArticleCategoryModal } from "@/pages/stammdaten/EditArticleCategoryModal";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import type { ArticleCategory } from "@/types/camping";

export function ArticleCategoriesPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWrite = canWriteStammdaten(groups);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ArticleCategory | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<ArticleCategory[]>("/api/v1/article-categories?include_inactive=true")
      .then(setCategories)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Artikelkategorien" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-label-secondary">
        Jede Kategorie hat ein Nummernschema mit <code className="text-label">#</code> als
        Platzhalter für die laufende Nummer (z.&nbsp;B. <code className="text-label">SP-####</code>
        → SP-0001, SP-0002).
      </p>

      <EditArticleCategoryModal
        open={!!selected}
        category={selected}
        onClose={() => setSelected(null)}
        onSaved={load}
        canEdit={canWrite}
      />
      <CreateArticleCategoryModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={load}
      />

      {!canWrite ? <StammdatenWriteHint /> : null}

      {canWrite ? (
        <div className="flex justify-end">
          <Button type="button" className="w-full sm:w-auto" onClick={() => setCreateOpen(true)}>
            Neue Artikelkategorie
          </Button>
        </div>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Laden…</p>
      ) : (
        <Table wide emptyMessage="Keine Artikelkategorien." isEmpty={categories.length === 0}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Nummernschema</th>
              <th>Sortierung</th>
              <th>Schnellverkauf</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((c) => (
              <ClickableTableRow
                key={c.id}
                ariaLabel={`Artikelkategorie ${c.name} öffnen`}
                onActivate={() => setSelected(c)}
              >
                <td className="font-medium">{c.name}</td>
                <td className="font-mono text-sm">{c.article_number_pattern}</td>
                <td>{c.sort_order}</td>
                <td>{c.for_quick_sale ? "Ja" : "—"}</td>
                <td>{c.is_active ? "Aktiv" : "Inaktiv"}</td>
              </ClickableTableRow>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
